import { ComboBox } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { Affiliate, ApplyCode, CouponType, isActive, isCouponCode, isGiftCode, isMembershipCode } from "types";
import "./ApplyCodes.css";
import { AnyRewardCode } from "types/src/types/RewardCode";
import { useVisibility } from "../../hooks/useVisibility";
import { NoTranslate } from "../Common/NoTranslate";
import { CouponSummary } from "./CouponList";
import { useCatalogItems } from "@/hooks/resources/useCatalogItems";

export interface ApplyCodesProps {
    onSelectCode: (code?: ApplyCode | null) => void;
    codes: AnyRewardCode[];
    onInputChange: (code: string) => void;
    affiliates: Affiliate[];
    inputValue: string | undefined;
}

export const ApplyCodes = ({ codes, inputValue, affiliates, onInputChange, onSelectCode }: ApplyCodesProps) => {
    const [isVisible, { show, hide }] = useVisibility(false);

    const [width, setWidth] = useState(0);
    const innerRef = useRef<HTMLDivElement>(null);

    const handleItemClick = (code: ApplyCode | null) => {
        onSelectCode(code);
        onInputChange(code ? code.code : "");
        hide();
    };

    const handleInputChange = (value: string, action: any) => {
        /** don't clear the input when we blur the input field */
        if (action.action !== "input-blur" && action.action !== "menu-close" && action.action !== "set-value") {
            onInputChange(value);
        }

        if (action.action === "set-value") {
            onInputChange(inputValue ?? "");
        }
    };

    const formatGroupLabel = ({ label }: { label: string }) => <span>{label}</span>;

    const filteredCouponCodes = codes?.filter((code) => isActive(code) && isCouponCode(code));
    const filteredGiftCodes = codes?.filter((code) => isActive(code) && isGiftCode(code));
    const filteredMembershipCodes = codes?.filter((code) => isActive(code) && isMembershipCode(code));

    const getOptionsByGroup = () => [
        {
            label: <CouponSectionHeader>Agents</CouponSectionHeader>,
            options: affiliates,
        },
        {
            label: <CouponSectionHeader className="!rounded-none">Coupons</CouponSectionHeader>,
            options: getOptions(filteredCouponCodes),
        },
        {
            label: <CouponSectionHeader className="!rounded-none">Gifts</CouponSectionHeader>,
            options: getOptions(filteredGiftCodes),
        },
        {
            label: <CouponSectionHeader className="!rounded-none">Memberships</CouponSectionHeader>,
            options: getOptions(filteredMembershipCodes),
        },
    ];

    const styles = {
        menuPortal: (base: any) => ({ ...base, zIndex: 30, display: "block" }),
        container: (css: any) => ({ ...css, width: `${width}px` }),
    };

    const value = inputValue ? inputValue : "";

    useEffect(() => {
        const currentRef = innerRef.current;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width } = entry.contentRect;
                setWidth(width);
            }
        });

        if (currentRef) {
            resizeObserver.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                resizeObserver.unobserve(currentRef);
            }
        };
    }, [innerRef]);

    return (
        <div ref={innerRef}>
            <ComboBox
                isClearable
                backspaceRemovesValue
                options={getOptionsByGroup()}
                inputValue={value}
                value={value}
                formatGroupLabel={formatGroupLabel}
                menuPortalTarget={document.body}
                menuPlacement="auto"
                getOptionValue={(option: any) => option.code}
                getOptionLabel={(option: any) => option.code}
                menuIsOpen={isVisible}
                closeMenuOnSelect={false}
                components={{ Option }}
                styles={styles}
                classNamePrefix="ui-apply-codes"
                placeholder="Choose a code"
                onChange={handleItemClick}
                onInputChange={handleInputChange}
                onBlur={hide}
                onFocus={show}
                onMenuOpen={show}
                onMenuClose={hide}
            />
        </div>
    );
};

interface OptionProps {
    data: ApplyCode;
    innerRef: any;
    innerProps: any;
}

const Option = ({ data, innerRef, innerProps }: OptionProps) => (
    <div
        ref={innerRef}
        className="cursor-pointer space-x-2.5 border-t border-gray-light p-4 align-text-top font-bold leading-4 hover:bg-gray-hover"
        {...innerProps}
    >
        <div className="flex w-full justify-between space-x-1">
            <NoTranslate as="div" className="min-w-20 flex-grow truncate">
                {data.code}
            </NoTranslate>
            <NoTranslate className="text-right font-normal tracking-tighter text-gray-darker">
                {data.amount ?? data.name}
            </NoTranslate>
        </div>
    </div>
);

export const CodeSummary = ({ code }: { code: AnyRewardCode }) => {
    const { data: catalogItems, isLoading } = useCatalogItems();

    if (isMembershipCode(code)) {
        return code.membershipItem ? <span>{code.membershipItem.name}</span> : null;
    }

    if (!code.coupon) {
        return <>N/A</>;
    }

    const demographicData = isLoading
        ? undefined
        : (catalogItems
              .map((item) => (item.template?.id ? { id: item.template.id, name: item.name } : undefined))
              .filter(Boolean) as { id: string; name: string }[]);

    return <CouponSummary coupon={code.coupon} demographicData={demographicData} />;
};

const CouponSectionHeader = ({ className, children }: { className?: string; children: React.ReactNode }) => {
    return (
        <div
            className={clsx(
                "rounded-tl rounded-tr bg-gray-light p-2 pl-3.5 text-base font-bold capitalize tracking-normal text-black",
                className,
            )}
        >
            {children}
        </div>
    );
};

const getOptions = (codes: AnyRewardCode[]) => {
    return (
        codes.map((code: AnyRewardCode) => ({
            ...code,
            type: isMembershipCode(code) ? CouponType.DISCOUNT : code.coupon.type,
            amount: <CodeSummary code={code} />,
        })) ?? []
    );
};
